import styled from '@emotion/styled'
import { ExtranetServiceDescriptionDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  MediaQuery,
  PageHeaderImage,
  htmlStringSanitizer,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacH4 } from '@pharmac/ui'
import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ExtranetParagraph } from '../../components/extranet/ExtranetParagraph'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'

const ServiceHeaderImage = styled.div`
  position: relative;
`

const HeaderTextContainer = styled.div`
  width: 80%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  align-content: center;

  padding: var(--spacing-medium) calc(var(--spacing-large) + var(--spacing-medium));
  border-radius: var(--border-radius-tiny);
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0.92) 45%,
    rgba(255, 255, 255, 0) 70%
  );

  li {
    line-height: calc(var(--typo-h6-size) * 1.5);
    font-size: large;
    width: 60%;
    padding-bottom: var(--spacing-medium);
  }

  ${MediaQuery.maxTablet} {
    width: 100%;
    position: relative;
    display: flex;
    padding: 1rem 0 1rem 2rem;
    top: 0;
    left: 0;
    background-color: var(--color-accent-2);

    li {
      width: 100%;
      line-height: calc(var(--typo-h6-size) * 1.5);
    }
  }
`

export const ServiceView = () => {
  const { slug } = useParams()

  const createLink = useExtranetPath()

  const { key } = useLanguage()

  const { customer } = useCustomerContext()
  const [service, setService] = useState<ExtranetServiceDescriptionDto>({
    id: 0,
    image: {
      desktop: '',
      desktopLarge: '',
      mobile: '',
    },
    slug: {
      fi: '',
      en: '',
      sv: '',
    },
    title: {
      fi: '',
      en: '',
      sv: '',
    },
    content: {
      fi: '',
      en: '',
      sv: '',
    },
    header: {
      fi: '',
      en: '',
      sv: '',
    },
    banner: {
      fi: '',
      en: '',
      sv: '',
    },
    experts: [],
  })

  const { data, isLoading, isFetched, isError, error } = useQuery({
    queryFn: async () => await api.services.getServiceBySlug(slug ?? '', customer?.id ?? 0),
    queryKey: ['__serviceDescription', slug, customer?.id],
    enabled: !!slug && typeof slug === 'string' && typeof customer?.id === 'number',
    retry: false,
  })

  const navigate = useNavigate()

  const { maxTablet } = useBreakpoint()

  useEffect(() => {
    if (!isLoading && isFetched && isError) {
      if (error) {
        const axiosErr = error as AxiosError
        if (axiosErr.response && axiosErr.response.status && axiosErr.response.status === 404) {
          return navigate(createLink('notfound'))
        }
      }
    }

    if (!isLoading && data) {
      setService(data)
    }
  }, [data, isFetched, isError, error, isLoading])

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        flexColumn
      >
        <ExtranetContainer
          full
          paddingLargeVertical
          paddingMediumHorizontal={!!maxTablet}
        >
          <PharmacH4 color="purple">{key(service.title)}</PharmacH4>
        </ExtranetContainer>
        <ExtranetContainer full>
          <ServiceHeaderImage>
            <PageHeaderImage
              image={service.image}
              rectangle={maxTablet}
            />
            <HeaderTextContainer
              dangerouslySetInnerHTML={{ __html: htmlStringSanitizer(key(service.banner)) }}
            />
          </ServiceHeaderImage>
        </ExtranetContainer>
        <ExtranetContainer
          full
          paddingLargeVertical
          paddingMediumHorizontal={!!maxTablet}
        >
          <PharmacH4 color="purple">{key(service.header)}</PharmacH4>
        </ExtranetContainer>
        <ExtranetContainer
          full
          paddingMediumHorizontal={!!maxTablet}
          paddingLargeBottom
        >
          <ExtranetParagraph>{htmlStringSanitizer(key(service.content))}</ExtranetParagraph>
        </ExtranetContainer>
      </ExtranetContainer>
    </ExtranetWrapper>
  )
}
